/**
 * Agent-side Twilio Voice integration. Mounted ONCE at the app root inside the
 * auth guard, but the Twilio `Device` is created LAZILY, per call:
 *
 *   - Dashboard CALL LEAD → /call-center/call-lead/{sessionName} route.
 *   - Direct call from anywhere (lead-info sidebar, lead profile, deal, etc.) → CallLeadPopup modal.
 *
 * Why lazy: the Voice token is now lead-scoped — `POST /voice/token` takes a
 * `{ leadId }` and returns `{ identity, token, sessionName }`. There's no lead
 * to mint a token for at provider-mount time, so the Device can only be built
 * once the agent actually starts a call. `connect()` does the full sequence:
 *   1) fetch the lead-scoped token,
 *   2) (re)create the Device with it (no register() — outbound-only),
 *   3) `device.connect({ params: { To, sessionName } })`.
 * The backend-returned `sessionName` (NOT a caller-supplied one) is what binds
 * the call to the lead's session lifecycle. Both entry points are therefore
 * session-linked now.
 *
 * Socket usage here is INBOUND only:
 *
 *   "call.ended"        IN  — listened to per-call. Backend emits this from
 *                        the Twilio dial-action statusCallback when the PSTN
 *                        leg disconnects. Robust signal we use to terminate
 *                        the local UI without waiting for the Voice SDK's
 *                        own (sometimes-delayed) `disconnect` event.
 *
 * INBOUND calls (calls TO the agent's own line) live in use-inbound-voice.ts:
 * a second, PERSISTENT Device (twilio-receiver.ts) stays registered as
 * `agent-{userId}` and delivers `incoming`; an accepted call plugs into this
 * provider's same callRef/finishCallRef machinery, so every consumer
 * (mute/hold/hangup, processors, indicator, popup) works for both directions.
 */
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Device, Call, type AudioProcessor } from "@twilio/voice-sdk";
import * as Sentry from "@sentry/react";
import { fetchTwilioAccessToken } from "@/services/call-center/twilio.token.service";
import { useSocketContext } from "@/context/socket.context/socket.context";
import { useInboundVoice, type IncomingCallInfo } from "@/context/use-inbound-voice";

export type TwilioCallState =
  | "idle"
  /** A call to the agent's own line is ringing their browser (inbound only). */
  | "incoming"
  | "initializing"
  | "ready"
  | "connecting"
  | "ringing"
  | "in-call"
  | "ended"
  | "error";

/**
 * Why a call ended, set on the terminal transition and surfaced via the
 * context for consumers that need it.
 */
export type CallEndReason = "completed" | "no-answer" | "rejected" | "failed";

/**
 * Agent-facing copy for each terminal reason. Centralized so the dedicated
 * call screen and the popup show the same wording — the whole point of
 * tracking the reason is that "the lead declined" reads differently from a
 * normal hangup.
 */
export const CALL_END_REASON_LABELS: Record<CallEndReason, string> = {
  completed: "Call ended",
  "no-answer": "No answer",
  rejected: "Lead declined the call",
  failed: "Call failed",
};

/**
 * Per-call context passed when starting an outbound call.
 *
 * `leadId`: REQUIRED. Used to mint the lead-scoped Voice token — the backend
 *   resolves/creates the call session from it and returns the `sessionName`
 *   we dial with. There is no token (and therefore no call) without it.
 *
 * The dialed `sessionName` comes back from the token response (not an input)
 * and is used internally for the dial params and the inbound `call.ended` match.
 */
export type ConnectOptions = {
  phoneNumber: string;
  leadId: number;
  /**
   * Display name shown by the floating ActiveCallIndicator when the agent
   * navigates away from the originating call surface. Optional but strongly
   * recommended — without it the indicator falls back to the phoneNumber.
   */
  leadName?: string;
  /**
   * Route path that originated the call (e.g. `/call-center/call-lead/{id}`).
   * The floating ActiveCallIndicator surfaces a "Return to call" button that
   * navigates here. Omit for popup-driven calls — the popup is its own
   * persistent indicator and doesn't need a return-to path.
   */
  originPath?: string;
  /**
   * Associates the call with a call center on the backend (token mint).
   * Only supplied when the call was initiated from a surface that has a
   * selected call center (e.g. the /call-center dashboard). Calls started
   * elsewhere (lead details, in-video handoff) omit it on purpose.
   */
  callCenterId?: number;
};

/**
 * Read-only call state. Split from the actions so components can subscribe to
 * exactly what they need: state consumers re-render on call transitions
 * (which they must), while action-only consumers (via useTwilioActions) never
 * re-render — the actions object is referentially stable for the provider's
 * lifetime.
 */
type TwilioStateProps = {
  state: TwilioCallState;
  errorMessage: string | null;
  /** True when the last (or current) error was caused by a lost internet connection. */
  isNetworkError: boolean;
  /** True while a live call is recovering from a temporary network loss. */
  isReconnecting: boolean;
  isMuted: boolean;
  /** True when the agent has placed the lead on hold. The mic is muted while on hold. */
  isOnHold: boolean;
  callStartedAt: Date | null;
  /**
   * True whenever a call is actively in progress — from the moment connect()
   * starts setting up (initializing the lead-scoped token + Device) through
   * connecting, ringing, and in-call. Used by call-trigger buttons across the
   * app to disable themselves so the agent can't start a second call while one
   * is live.
   */
  isCallActive: boolean;
  /**
   * Why the last call ended, set on the terminal transition (null until then,
   * cleared when a new call starts).
   */
  callEndReason: CallEndReason | null;
  /** Display name for the active call's lead, used by the floating indicator. */
  leadName: string | null;
  /** E.164 phone for the active call, fallback display when leadName absent. */
  phoneNumber: string | null;
  /** Origin route to navigate back to via the indicator's Return button. */
  originPath: string | null;
  /** leadId for the active call's lead — used by the indicator to pass state when returning. */
  leadId: number | null;
  /** Twilio session name for the active call — used by the indicator to pass state when returning. */
  sessionName: string | null;
  /** A ringing inbound call awaiting accept/reject; null when nothing rings. */
  incomingCall: IncomingCallInfo | null;
  /**
   * Which way the current (or just-ended) call goes. Set when a call starts,
   * cleared by resetCallState — surfaces key off it (e.g. the popup provider
   * auto-opens for an accepted inbound call from a known lead).
   */
  direction: "inbound" | "outbound" | null;
};

/** Imperative call controls. All referentially stable (useCallback). */
type TwilioActionsProps = {
  connect: (opts: ConnectOptions) => Promise<void>;
  mute: () => void;
  unmute: () => void;
  /** Place the lead on hold: mutes the agent's mic so the lead hears silence. */
  hold: () => void;
  /** Take the lead off hold and restore audio. */
  unhold: () => void;
  hangup: () => void;
  addAudioProcessor: (processor: AudioProcessor, isRemote: boolean) => Promise<void>;
  removeAudioProcessor: (processor: AudioProcessor, isRemote: boolean) => Promise<void>;
  /**
   * Reset state back to "idle" after a terminal call so the next call starts
   * fresh. Lets a UI surface close itself (popup, dedicated route) and free
   * the slot for the next call.
   */
  resetCallState: () => void;
  /** Answer the ringing inbound call — it becomes THE active call. */
  acceptIncoming: () => void;
  /** Decline the ringing inbound call (caller hears the unavailable message). */
  rejectIncoming: () => void;
  /** Answer the ringing call as another lead from `incomingCall.leadMatches`. */
  selectIncomingLead: (leadId: number) => void;
  /** Re-check `GET /phone-number/me` and register/unregister the inbound receiver. */
  refreshReceiver: () => void;
};

type TwilioContextProps = TwilioStateProps & TwilioActionsProps;

const TwilioStateContext = createContext<TwilioStateProps | null>(null);
const TwilioActionsContext = createContext<TwilioActionsProps | null>(null);

// Console prefix for the warn-level logs we keep in production (quality
// warnings, ignored-duplicate-terminal events, audio-constraints failure).
// Useful when an agent screen-shares their console to debug a call issue.
const LOG = "[Twilio]";

/** How long a live call may stay offline before it is given up as failed. */
const RECONNECT_GRACE_MS = 60_000;

/**
 * Device-level signaling errors that are transient while a call is live (the
 * SDK recovers on its own once the network returns): 53000 signaling
 * connection error, 53001 signaling connection disconnected.
 */
const TRANSIENT_DEVICE_ERROR_CODES = new Set([53000, 53001]);

export const TwilioProvider = ({ children }: PropsWithChildren) => {
  const socket = useSocketContext();
  const deviceRef = useRef<Device | null>(null);
  // The Device that owns the LIVE call's media: the ephemeral outbound Device,
  // or the persistent inbound receiver's. Audio processors (sentiment /
  // transcription) attach through this — deviceRef alone would miss inbound.
  const activeDeviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  // Synchronous guard against duplicate `connect()` invocations (e.g. user
  // double-clicks "Start Call"). React state alone is too slow — the second
  // click fires before the first `setState("connecting")` has rendered. The
  // ref is set/cleared inside connect()'s lifecycle so any concurrent call
  // sees it before its own setState.
  const callInFlightRef = useRef(false);
  // Holds the teardown for the per-call `socket.on("call.ended", ...)`
  // listener so finishCall() AND the provider's unmount cleanup can both
  // remove it. Without this, a connect()→unmount path (e.g. agent navigates
  // away mid-call) would leak a listener bound to the closed call's state.
  const cleanupSocketRef = useRef<(() => void) | null>(null);
  // Points at the ACTIVE call's finishCall (set after dial, cleared on
  // terminal). The Device "error" handler is registered before finishCall
  // exists, so it reaches the current call's teardown through this ref —
  // without it, a device-level error mid-call would set state="error" while
  // leaving callInFlightRef stuck true, silently bricking the next
  // "Start Call" until a page refresh.
  const finishCallRef = useRef<
    ((reason: CallEndReason, nextState: "ended" | "error", source: string) => boolean) | null
  >(null);
  // Mirror of isMuted state as a ref so hold() / unhold() can read the live
  // mute value without a stale closure — the mute event handler keeps it in sync.
  const currentMutedRef = useRef(false);
  // Mute state captured at the moment hold() is invoked, restored by unhold()
  // so an agent who was already muted before going on hold stays muted after.
  const preHoldMutedRef = useRef(false);

  // When true, the terminal "error" was caused by a lost internet connection.
  // Cleared by resetCallState() so the next call starts with a clean flag.
  const networkTerminatedRef = useRef(false);
  // Grace timer started when the browser goes offline mid-call; if the network
  // is still down when it fires, the call is terminated as a network error.
  const offlineTimerRef = useRef<number | null>(null);

  // Set by hangup() when the agent cancels while connect() is still awaiting
  // (token mint, mic permission) and no Call object exists yet to disconnect.
  // connect() checks it after each await and bails silently — without this,
  // closing the call surface during "initializing" lets the dial proceed
  // headless and the lead's phone rings anyway.
  const abortConnectRef = useRef(false);

  const [state, setState] = useState<TwilioCallState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [callStartedAt, setCallStartedAt] = useState<Date | null>(null);
  const [callEndReason, setCallEndReason] = useState<CallEndReason | null>(null);
  // Per-call identity surfaced to the global indicator. Cleared by
  // resetCallState() so a stale lead name doesn't show up on the next call.
  const [activeLeadName, setActiveLeadName] = useState<string | null>(null);
  const [activePhoneNumber, setActivePhoneNumber] = useState<string | null>(null);
  const [activeOriginPath, setActiveOriginPath] = useState<string | null>(null);
  const [activeLeadId, setActiveLeadId] = useState<number | null>(null);
  const [activeSessionName, setActiveSessionName] = useState<string | null>(null);
  const [callDirection, setCallDirection] = useState<"inbound" | "outbound" | null>(null);

  // Provider-unmount cleanup: if the provider unmounts mid-call (e.g. agent
  // logs out), tear down the live call, device, and the per-call socket
  // listener so nothing leaks.
  useEffect(() => {
    return () => {
      if (cleanupSocketRef.current) {
        cleanupSocketRef.current();
        cleanupSocketRef.current = null;
      }
      if (callRef.current) {
        try { callRef.current.disconnect(); } catch { /* noop */ }
        callRef.current = null;
      }
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch { /* noop */ }
        deviceRef.current = null;
      }
      activeDeviceRef.current = null;
    };
  }, []);

  // Monitor internet connectivity during an active call. When the browser
  // fires "offline", we flag the termination as network-driven so finishCall
  // (triggered by the subsequent Twilio `disconnect` event) surfaces the error
  // state with a friendly message instead of the generic "Call ended" outcome.
  useEffect(() => {
    const clearTimer = () => {
      if (offlineTimerRef.current) {
        window.clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }
    };
    const handleOffline = () => {
      // Only act when a call is live. If no Call object exists yet (e.g. still
      // minting the token), the connect() catch block will detect offline state
      // on its own and show the same friendly message.
      if (!callRef.current || !callInFlightRef.current) return;
      // Don't drop the call: the Voice SDK reconnects signaling and restarts
      // ICE once the network is back. Show "reconnecting" and only give up
      // after a grace period.
      setIsReconnecting(true);
      clearTimer();
      offlineTimerRef.current = window.setTimeout(() => {
        offlineTimerRef.current = null;
        if (navigator.onLine || !callRef.current || !callInFlightRef.current) return;
        networkTerminatedRef.current = true;
        setIsNetworkError(true);
        setErrorMessage("No internet connection. Your call has been disconnected.");
        // finishCall (via the call's "disconnect" event) picks up
        // networkTerminatedRef and transitions to "error".
        try { callRef.current.disconnect(); } catch { /* noop */ }
      }, RECONNECT_GRACE_MS);
    };
    const handleOnline = () => {
      clearTimer();
      // If the SDK is not mid-reconnect, the call survived — clear the banner.
      // Otherwise the call's "reconnected" event clears it.
      if (callRef.current?.status() !== "reconnecting") setIsReconnecting(false);
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      clearTimer();
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  // Call went terminal (or was reset): drop the reconnecting banner and timer.
  useEffect(() => {
    if (state === "in-call" || state === "connecting" || state === "ringing") return;
    setIsReconnecting(false);
    if (offlineTimerRef.current) {
      window.clearTimeout(offlineTimerRef.current);
      offlineTimerRef.current = null;
    }
  }, [state]);

  /**
   * Shared pre-call reset + identity publish, used by the inbound accept path
   * (and intentionally NOT retrofitted into connect(), whose intro block
   * predates it — keep the outbound flow untouched).
   */
  const beginCall = useCallback(
    (identity: {
      leadName: string | null;
      phoneNumber: string | null;
      leadId: number | null;
      sessionName: string | null;
    }) => {
      setErrorMessage(null);
      setIsNetworkError(false);
      setIsMuted(false);
      setIsOnHold(false);
      setCallStartedAt(null);
      setCallEndReason(null);
      currentMutedRef.current = false;
      preHoldMutedRef.current = false;
      networkTerminatedRef.current = false;
      setActiveLeadName(identity.leadName);
      setActivePhoneNumber(identity.phoneNumber);
      setActiveOriginPath(null);
      setActiveLeadId(identity.leadId);
      setActiveSessionName(identity.sessionName);
      // beginCall is only ever the inbound accept's entry point today (see the
      // header comment) — parameterize this if outbound ever adopts it.
      setCallDirection("inbound");
    },
    []
  );

  // Inbound half of the state machine: the persistent receiver Device, the
  // ringing `incomingCall`, and accept/reject. An accepted call lands in the
  // same callRef/finishCallRef machinery the outbound path drives.
  const { incomingCall, acceptIncoming, rejectIncoming, selectIncomingLead, refreshReceiver } = useInboundVoice({
    callRef,
    callInFlightRef,
    finishCallRef,
    cleanupSocketRef,
    currentMutedRef,
    networkTerminatedRef,
    activeDeviceRef,
    beginCall,
    setState,
    setErrorMessage,
    setIsMuted,
    setIsReconnecting,
    setCallStartedAt,
    setCallEndReason,
  });

  const connect = useCallback(async (opts: ConnectOptions) => {
    // Synchronous dedup. Cleared by the call's terminal events (disconnect,
    // cancel, reject, error) or the catch block below if connect() throws
    // before a Call object exists.
    if (callInFlightRef.current) return;
    callInFlightRef.current = true;
    abortConnectRef.current = false;
    // Dialing out while an inbound call is still ringing means the agent chose
    // the outbound call — decline the ring so it doesn't sit in limbo.
    rejectIncoming();

    // Capture per-call context into closure-stable consts for the event
    // handlers below. They reference these instead of the provider-level
    // refs so the call's identity is fixed at dial-time even if a parallel
    // call attempt mutates state. `sessionName` is filled in from the token
    // response once we have it.
    const { phoneNumber, leadId, leadName, originPath, callCenterId } = opts;
    let sessionName = "";

    try {
      setErrorMessage(null);
      setIsNetworkError(false);
      setIsMuted(false);
      setIsOnHold(false);
      setCallStartedAt(null);
      setCallEndReason(null);
      currentMutedRef.current = false;
      preHoldMutedRef.current = false;
      // "initializing" while we mint the token + register the Device, then
      // "connecting" once we actually dial (below).
      setState("initializing");
      // Surface call identity to the global indicator. Defaults to the raw
      // phone number when no leadName was provided so we never render a
      // blank widget.
      setActiveLeadName(leadName ?? null);
      setActivePhoneNumber(phoneNumber);
      setActiveOriginPath(originPath ?? null);
      setActiveLeadId(leadId);
      setCallDirection("outbound");

      // Bails out of an aborted connect(): tears down whatever exists so far
      // (a Call when passed, the Device) and returns to "idle" SILENTLY — the
      // surface that requested the abort has already closed, so no terminal
      // state should linger for it to render.
      const bailIfAborted = (call?: Call): boolean => {
        if (!abortConnectRef.current) return false;
        abortConnectRef.current = false;
        if (call) {
          try { call.disconnect(); } catch { /* noop */ }
        }
        if (deviceRef.current) {
          try { deviceRef.current.destroy(); } catch { /* noop */ }
          deviceRef.current = null;
        }
        activeDeviceRef.current = null;
        finishCallRef.current = null;
        callInFlightRef.current = false;
        setActiveLeadName(null);
        setActivePhoneNumber(null);
        setActiveOriginPath(null);
        setActiveLeadId(null);
        setActiveSessionName(null);
        setState("idle");
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "info",
          message: "connect() aborted by agent before call setup completed",
          data: { leadId, phoneNumber },
        });
        return true;
      };

      Sentry.addBreadcrumb({
        category: "twilio.device",
        level: "info",
        message: "Requesting lead-scoped Voice token",
        data: { leadId, phoneNumber },
      });

      // 1) Mint the lead-scoped token. The backend resolves/creates the call
      //    session and returns the `sessionName` we dial with. `callCenterId`
      //    (when the originating surface provided one) associates the call
      //    with that call center.
      const tokenResponse = await fetchTwilioAccessToken(leadId, callCenterId);
      if (bailIfAborted()) return;
      sessionName = tokenResponse.sessionName;
      setActiveSessionName(sessionName || null);
      const isLinkedToSession = !!sessionName;

      // 2) (Re)create the Device with the freshly-minted token. Destroy any
      //    stale device from a prior call first so we never stack registered
      //    devices.
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch { /* noop */ }
        deviceRef.current = null;
      }

      // ─── Audio quality tuning ─────────────────────────────────────────
      //
      // 1) `codecPreferences: [Opus, PCMU]` — Opus is wider band and has
      //    much better packet-loss concealment than the default PCMU
      //    (G.711). The PSTN leg still transcodes to PCMU at Twilio, so
      //    the lead doesn't hear wideband audio, but the WebRTC leg
      //    (agent ↔ Twilio) becomes much more resilient to jitter / loss,
      //    which is usually where the perceptual quality drop comes from.
      //
      // 2) `maxAverageBitrate: 16000` — bumps Opus from its default ~8 kbps
      //    voice floor. 16 kbps is the sweet spot for outbound voice: enough
      //    headroom for clean encoding without flooding the uplink.
      //
      // 3) `edge: "roaming"` — explicit default. Twilio auto-selects the
      //    closest edge from this list; switch to a specific one like
      //    `"ashburn"` if you can measure better latency to a particular
      //    region from your typical agent location.
      const device = new Device(tokenResponse.token, {
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        maxAverageBitrate: 16000,
        edge: "roaming",
        // Without this flag, a gateway HANGUP that carries a SIP-level cause
        // (31603 Decline, 31486 Busy, 31480 No Answer...) gets collapsed into
        // the generic ConnectionError 31005, hiding the real outcome. With it,
        // the SDK surfaces the precise error so the `error` handler below can
        // tell "lead declined" apart from an actual connection failure.
        enableImprovedSignalingErrorPrecision: true,
      });

      // Force the browser's getUserMedia constraints to enable echo
      // cancellation, noise suppression, and auto gain control. Most
      // browsers default these to ON for voice, but being explicit avoids
      // surprises when a user has overridden defaults via OS audio drivers
      // or a Bluetooth headset that exposes the raw mic. Fire-and-forget
      // — failures don't block device init (we just lose the tuning).
      device.audio?.setAudioConstraints({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      }).catch((err) => {
        console.warn(`${LOG} setAudioConstraints failed`, err);
      });

      device.on("error", (err: { message?: string; code?: number }) => {
        Sentry.captureException(
          err instanceof Error ? err : new Error(`[Twilio Device] ${err?.message ?? "Unknown device error"}`),
          {
            tags: { component: "TwilioContext", source: "Device.on(error)" },
            extra: { twilioCode: err?.code, message: err?.message },
          },
        );
        // A call is live → route through ITS finishCall so the in-flight
        // guard, socket listener, and end-reason are torn down exactly once.
        // finishCall returns false after terminal, so a late device error
        // (e.g. token expiry after hangup) can't clobber the final state or
        // the message the terminal path already surfaced.
        const finish = finishCallRef.current;
        if (finish) {
          // Transient signaling loss mid-call: the call is reconnecting, not dead.
          if (TRANSIENT_DEVICE_ERROR_CODES.has(err?.code ?? -1) && callRef.current) {
            setIsReconnecting(true);
            return;
          }
          if (finish("failed", "error", "device:error")) {
            setErrorMessage(err?.message ?? "Device error");
          }
          return;
        }
        // No Call yet (token mint / register / dial still in flight): the
        // awaited step rejects into connect()'s catch block, which owns the
        // cleanup. Just surface the error state.
        setErrorMessage(err?.message ?? "Device error");
        setState("error");
      });

      // Token refresh reuses THIS call's leadId — the token is lead-scoped,
      // so there's no generic token to fall back on.
      device.on("tokenWillExpire", async () => {
        Sentry.addBreadcrumb({
          category: "twilio.device",
          level: "info",
          message: "Token will expire — refreshing",
        });
        try {
          const refreshed = await fetchTwilioAccessToken(leadId, callCenterId);
          device.updateToken(refreshed.token);
        } catch (e) {
          Sentry.captureException(e, {
            tags: { component: "TwilioContext", operation: "tokenRefresh" },
          });
        }
      });

      // No device.register() — registration only enrolls the Device to RECEIVE
      // incoming calls, which this outbound-only provider never does. connect()
      // lazily establishes the signaling stream itself, and the SDK arms the
      // tokenWillExpire timer on signaling-connect (not on registration), so
      // the refresh handler above keeps working. Skipping it removes a full
      // signaling round-trip from the agent's click-to-dial critical path.
      deviceRef.current = device;
      activeDeviceRef.current = device;

      // 3) Dial.
      if (bailIfAborted()) return;
      setState("connecting");
      Sentry.addBreadcrumb({
        category: "twilio.call",
        level: "info",
        message: "Initiating outbound call",
        data: { phoneNumber, sessionName, leadId, isLinkedToSession },
      });

      // The backend-returned `sessionName` ties the call to the lead's
      // session so the post-dial status callback can drive the lead's
      // IncomingCallScreen via WS events.
      const call = await device.connect({
        params: {
          To: phoneNumber,
          sessionName,
        },
      });

      // device.connect() can stall on the mic-permission prompt — the agent
      // may have cancelled while it was pending. No handlers are registered
      // on `call` yet, so bail disconnects it directly.
      if (bailIfAborted(call)) return;

      callRef.current = call;

      // ─── Terminal-state guard ─────────────────────────────────────────
      // Centralizes the cleanup so every path that ends a call (disconnect,
      // cancel, reject, error, or the backend telling us via socket) goes
      // through the SAME sequence and is idempotent. The guard means even
      // if Twilio fires `disconnect` twice (or both `cancel` and `disconnect`
      // race), the socket emit and state transition only happen once.
      // Duplicate terminal events after the first are surfaced as a warn so
      // they don't go silent.
      let terminated = false;
      // Returns true when it performed the terminal transition, false when the
      // call was already terminal (so callers can skip their own side effects,
      // e.g. the device error handler skipping setErrorMessage).
      const finishCall = (
        reason: "completed" | "no-answer" | "rejected" | "failed",
        nextState: "ended" | "error",
        source: string,
      ): boolean => {
        if (terminated) {
          console.warn(`${LOG} call.${source} fired AFTER terminal (ignored)`);
          return false;
        }
        terminated = true;
        // This call is over — stop routing device errors through it.
        finishCallRef.current = null;
        // Tear down the backend-driven listener so we don't leak it (and so
        // a future call doesn't accidentally inherit a stale handler).
        socket.off("call.ended", onCallEndedFromBackend);
        cleanupSocketRef.current = null;
        // Network-loss terminations always surface as an error regardless of
        // what Twilio reports. The `disconnect` event fires with reason
        // "completed" after we explicitly disconnect on offline, but the real
        // cause is the lost connection, so we override both the reason and
        // the next state here. Reset the flag so the next call starts clean.
        const effectiveReason = networkTerminatedRef.current ? "failed" : reason;
        const effectiveNextState = networkTerminatedRef.current ? "error" : nextState;
        // When the call ended due to network loss, destroy the Device now so
        // it cannot attempt to re-register when connectivity returns. Without
        // this, the Device re-connects to Twilio's signaling server, the stale
        // token is rejected, and device.on("error") fires with
        // "AccessTokenInvalid (20101)" — overwriting the friendly "No internet"
        // error message we already set.
        if (networkTerminatedRef.current && deviceRef.current) {
          try { deviceRef.current.destroy(); } catch { /* noop */ }
          deviceRef.current = null;
        }
        networkTerminatedRef.current = false;
        // Release the microphone. Two holders can keep the browser's mic
        // indicator lit after a call ends:
        //   1. The call's local MediaStream (on the socket-driven terminal
        //      path the SDK call may never fire its own `disconnect`, leaving
        //      a zombie WebRTC leg with live mic tracks).
        //   2. The Device's AudioHelper streams (default/selected input +
        //      processed) — destroy() → audio._destroy() stops them all and
        //      invokes each attached processor's destroyProcessedStream.
        // A third holder is prevented at the source: removeAudioProcessor()
        // skips SDK processor removal once the call is terminal, because that
        // removal re-acquires the mic via a fresh getUserMedia (see its
        // comment). Deferred a tick because finishCall often runs INSIDE one
        // of this device's own event dispatches; the instance-capture guard
        // means a fast redial's fresh Device (connect() replaces the ref) is
        // never touched — worst case the stale device gets a second
        // destroy(), which the try/catch absorbs.
        const staleDevice = deviceRef.current;
        const staleLocalStream = callRef.current?.getLocalStream();
        if (staleDevice || staleLocalStream) {
          window.setTimeout(() => {
            if (deviceRef.current === staleDevice) {
              deviceRef.current = null;
            }
            try {
              staleLocalStream?.getTracks().forEach((track) => track.stop());
            } catch { /* noop */ }
            try { staleDevice?.destroy(); } catch { /* noop */ }
          }, 0);
        }
        // Record WHY the call ended so consumers can read it off the context.
        setCallEndReason(effectiveReason);
        callRef.current = null;
        activeDeviceRef.current = null;
        callInFlightRef.current = false;
        setState(effectiveNextState);
        return true;
      };
      finishCallRef.current = finishCall;

      // ─── Backend-driven terminal event ────────────────────────────────
      // The Twilio Voice SDK does NOT reliably fire `disconnect` when the
      // PSTN side hangs up (the `<Dial>` answerOnBridge path inside Twilio's
      // bridge does not always propagate the BYE back to the WebRTC client).
      // The backend works around this by registering a TwiML `<Dial action>`
      // statusCallback — when the dialed leg ends, the backend emits this
      // socket event so we can close the call from the agent's side too.
      //
      // The payload carries `sessionName`; we only act when it matches the
      // currently active call so a stale event from a previous call can't
      // terminate this one.
      const onCallEndedFromBackend = (payload?: { sessionName?: string; reason?: string }) => {
        if (!isLinkedToSession) return;
        if (payload?.sessionName && payload.sessionName !== sessionName) return;
        // "rejected" comes through when the backend can tell the lead
        // declined; "voicemail" (pending backend AMD support) means the
        // carrier diverted a declined/unanswered call to voicemail — the
        // lead never picked up, so surface it as no-answer.
        const mapped: CallEndReason =
          payload?.reason === "no-answer" ||
          payload?.reason === "failed" ||
          payload?.reason === "rejected"
            ? payload.reason
            : payload?.reason === "voicemail"
              ? "no-answer"
              : "completed";
        finishCall(mapped, mapped === "failed" ? "error" : "ended", "socket:call.ended");
      };
      socket.on("call.ended", onCallEndedFromBackend);
      cleanupSocketRef.current = () => socket.off("call.ended", onCallEndedFromBackend);

      call.on("ringing", () => {
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "info",
          message: "Call ringing on remote end",
          data: { sessionName, leadId },
        });
        setState("ringing");
      });

      call.on("accept", () => {
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "info",
          message: "Call accepted (lead picked up)",
          data: { sessionName, leadId },
        });
        setCallStartedAt(new Date());
        setState("in-call");
      });

      // `disconnect` is the canonical event for call-end from EITHER side
      // (remote PSTN hangup, agent hangup, network drop after accept).
      // Twilio Voice SDK v2 fires it on the Call instance.
      call.on("disconnect", () => {
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "info",
          message: "Call disconnected (completed)",
          data: { sessionName, leadId },
        });
        finishCall("completed", "ended", "disconnect");
      });

      // SDK-driven recovery: media/signaling dropped, ICE restart in progress.
      call.on("reconnecting", () => {
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "warning",
          message: "Call reconnecting",
          data: { sessionName, leadId },
        });
        setIsReconnecting(true);
      });
      call.on("reconnected", () => {
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "info",
          message: "Call reconnected",
          data: { sessionName, leadId },
        });
        setIsReconnecting(false);
      });

      call.on("cancel", () => {
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "info",
          message: "Call cancelled (no answer)",
          data: { sessionName, leadId },
        });
        finishCall("no-answer", "ended", "cancel");
      });

      // ─── Error vs. normal-outcome triage ──────────────────────────────
      // For OUTBOUND calls the SDK never fires `reject` — a remote decline
      // arrives as a gateway HANGUP with a SIP cause code that surfaces
      // through THIS handler. Those are normal call outcomes, not failures:
      //   31603 Decline                → lead rejected the call
      //   31486 Busy Here              → lead is on another call
      //   31480 Temporarily Unavailable / 31487 Request Terminated → no answer
      // We read the code from the error itself and, while the improved
      // precision flag rolls out, fall back to `originalError.code` (the raw
      // gateway payload the generic 31005 wraps).
      //
      // TODO(contract): "rejected" isn't in the agent:disengaged contract's
      // enum yet (allowed: completed | denied | no-answer | failed | left).
      // Pending alignment with backend on whether to extend the enum or map
      // it to "denied".
      const HUMAN_HANGUP_CODES: Record<number, CallEndReason> = {
        31603: "rejected",
        31486: "rejected",
        31480: "no-answer",
        31487: "no-answer",
      };
      call.on(
        "error",
        (err: { message?: string; code?: number; originalError?: { code?: number } }) => {
          const causeCode = HUMAN_HANGUP_CODES[err?.code ?? -1] !== undefined
            ? err?.code
            : err?.originalError?.code;
          const humanReason = causeCode != null ? HUMAN_HANGUP_CODES[causeCode] : undefined;
          if (humanReason) {
            Sentry.addBreadcrumb({
              category: "twilio.call",
              level: "info",
              message: `Call ended by remote cause ${causeCode} (${humanReason})`,
              data: { sessionName, leadId, twilioCode: causeCode },
            });
            finishCall(humanReason, "ended", `error:${causeCode}`);
            return;
          }
          Sentry.captureException(
            err instanceof Error ? err : new Error(`[Twilio Call] ${err?.message ?? "Unknown call error"}`),
            {
              tags: { component: "TwilioContext", source: "Call.on(error)" },
              extra: { sessionName, leadId, phoneNumber, twilioCode: err?.code, message: err?.message },
            },
          );
          setErrorMessage(err?.message ?? "Call error");
          finishCall("failed", "error", "error");
        },
      );

      call.on("mute", (muted: boolean) => {
        Sentry.addBreadcrumb({
          category: "twilio.call",
          level: "info",
          message: muted ? "Agent muted the call" : "Agent unmuted the call",
          data: { sessionName, leadId },
        });
        setIsMuted(muted);
        currentMutedRef.current = muted;
      });

      // ─── Quality warnings ─────────────────────────────────────────────
      // The Voice SDK continuously samples jitter, RTT, packet loss, audio
      // levels, and MOS. It fires `warning` when a metric crosses a bad
      // threshold and `warning-cleared` when it recovers. We log warnings
      // to the console so an agent screen-sharing their dev tools can show
      // them in real time, and breadcrumb to Sentry so they surface in any
      // post-hoc error report.
      //
      // Common warnings: high-jitter, high-rtt, high-packet-loss-fraction,
      // low-mos, low-bytes-received, low-audio-level, constant-audio-input-level.
      call.on("warning", (warningName: string, warningData?: unknown) => {
        console.warn(`${LOG} call.warning: ${warningName}`, warningData);
        Sentry.addBreadcrumb({
          category: "twilio.call.quality",
          level: "warning",
          message: `Quality warning: ${warningName}`,
          data: { sessionName, leadId, warningData },
        });
      });
      call.on("warning-cleared", (warningName: string) => {
        Sentry.addBreadcrumb({
          category: "twilio.call.quality",
          level: "info",
          message: `Quality warning cleared: ${warningName}`,
          data: { sessionName, leadId },
        });
      });
    } catch (err) {
      // Surface a friendly message when we know the failure is connectivity:
      // navigator.onLine === false is a reliable signal here because a network
      // error during token fetch or device.connect() is almost always a dropped
      // connection, and both signals agree.
      const offline = !navigator.onLine;
      const message = offline
        ? "No internet connection. Please check your network and try again."
        : err instanceof Error ? err.message : "Failed to start call";
      Sentry.captureException(err, {
        tags: { component: "TwilioContext", operation: "connect", offline: String(offline) },
        extra: { sessionName, leadId, phoneNumber },
      });
      // Tear down any device we managed to create before the failure (token
      // fetch, register, or dial could each throw) so a retry starts clean.
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch { /* noop */ }
        deviceRef.current = null;
      }
      activeDeviceRef.current = null;
      // Release the in-flight guard so the agent can retry. We never got a
      // Call object back from Twilio, so there are no call event handlers
      // that would clear the ref for us. Drop any finishCall published before
      // the throw for the same reason — it belongs to this aborted attempt.
      finishCallRef.current = null;
      callInFlightRef.current = false;
      if (offline) setIsNetworkError(true);
      setErrorMessage(message);
      setCallEndReason("failed");
      setState("error");
    }
  }, [socket, rejectIncoming]);

  const mute = useCallback(() => {
    callRef.current?.mute(true);
  }, []);

  const unmute = useCallback(() => {
    callRef.current?.mute(false);
  }, []);

  const hold = useCallback(() => {
    // Save the agent's current mute state so unhold() can restore it. Without
    // this, an agent who was already muted before holding gets unexpectedly
    // unmuted when they resume the call.
    preHoldMutedRef.current = currentMutedRef.current;
    callRef.current?.mute(true);
    setIsOnHold(true);
  }, []);

  const unhold = useCallback(() => {
    // Restore the mute state the agent had before placing the lead on hold.
    callRef.current?.mute(preHoldMutedRef.current);
    setIsOnHold(false);
  }, []);

  const hangup = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect();
      return;
    }
    // No Call object yet, but a connect() is mid-flight (token mint, mic
    // permission prompt): request an abort — connect() bails at its next
    // checkpoint instead of dialing a lead the agent already cancelled on.
    if (callInFlightRef.current) {
      abortConnectRef.current = true;
    }
  }, []);

  // After a terminal state ("ended" / "error"), call surfaces close themselves
  // (popup unmounts, route navigates away) and invoke resetCallState to drop
  // back to "idle" so the next call can start fresh.
  //
  // We deliberately do NOT destroy the per-call Device here. The popup's
  // handleClose calls hangup() then resetCallState() synchronously; the Call's
  // `disconnect` event (which runs finishCall — clearing the in-flight guard
  // and setting the terminal state) fires asynchronously AFTER. Destroying the
  // Device synchronously could pre-empt that event and strand the call as
  // in-flight. Device teardown belongs to finishCall itself (deferred a tick),
  // which releases the microphone as soon as the call is terminal; connect()'s
  // stale-device cleanup and provider unmount remain the backstops.
  // Processors attach to the Device that owns the LIVE call — the ephemeral
  // outbound one or the inbound receiver's — hence activeDeviceRef, not deviceRef.
  const addAudioProcessor = useCallback(async (processor: AudioProcessor, isRemote: boolean) => {
    if (!activeDeviceRef.current?.audio) return;
    await activeDeviceRef.current.audio.addProcessor(processor, isRemote);
  }, []);

  const removeAudioProcessor = useCallback(async (processor: AudioProcessor, isRemote: boolean) => {
    if (!activeDeviceRef.current?.audio) return;
    // Once the call is terminal, DON'T remove processors through the SDK.
    // AudioHelper.removeProcessor on a LOCAL processor runs
    // _restartInputStreams() → _setInputDevice(default, forceGetUserMedia:
    // true) — a FRESH getUserMedia that re-lights the browser's mic
    // indicator with no call left to use it. It also races finishCall's
    // deferred device teardown: when destroy() wins, the reacquired stream
    // lands on an already-destroyed helper that will never stop it, leaving
    // the mic permanently "in use". Skipping loses nothing — destroy() runs
    // audio._destroy(), which stops all streams and destroys the processed
    // streams itself, invoking each processor's destroyProcessedStream.
    // Mid-call removal (e.g. the sentiment session failed to start) keeps
    // the real SDK path: the restart is what feeds the live call the raw,
    // unprocessed stream again.
    if (!callInFlightRef.current) return;
    await activeDeviceRef.current.audio.removeProcessor(processor, isRemote);
  }, []);

  const resetCallState = useCallback(() => {
    setState((prev) => (prev === "ended" || prev === "error" ? "idle" : prev));
    setErrorMessage(null);
    setIsNetworkError(false);
    setIsMuted(false);
    setIsOnHold(false);
    setCallStartedAt(null);
    setCallEndReason(null);
    setActiveLeadName(null);
    setActivePhoneNumber(null);
    setActiveOriginPath(null);
    setActiveLeadId(null);
    setActiveSessionName(null);
    setCallDirection(null);
    currentMutedRef.current = false;
    preHoldMutedRef.current = false;
  }, []);

  const isCallActive =
    state === "initializing" ||
    state === "connecting" ||
    state === "ringing" ||
    state === "in-call";

  // Memoized so the state context only changes identity when a call-state
  // field actually changes — a parent-driven re-render of the provider (theme,
  // snackbar, etc.) no longer cascades into every call-state consumer.
  const stateValue = useMemo<TwilioStateProps>(
    () => ({
      state,
      errorMessage,
      isNetworkError,
      isReconnecting,
      isMuted,
      isOnHold,
      callStartedAt,
      isCallActive,
      callEndReason,
      leadName: activeLeadName,
      phoneNumber: activePhoneNumber,
      originPath: activeOriginPath,
      leadId: activeLeadId,
      sessionName: activeSessionName,
      incomingCall,
      direction: callDirection,
    }),
    [
      state,
      errorMessage,
      isNetworkError,
      isReconnecting,
      isMuted,
      isOnHold,
      callStartedAt,
      isCallActive,
      callEndReason,
      activeLeadName,
      activePhoneNumber,
      activeOriginPath,
      activeLeadId,
      activeSessionName,
      incomingCall,
      callDirection,
    ]
  );

  // Every action is a stable useCallback (connect's only dep is the singleton
  // socket), so this object never changes identity: action-only consumers
  // never re-render on call-state transitions.
  const actionsValue = useMemo<TwilioActionsProps>(
    () => ({
      connect,
      mute,
      unmute,
      hold,
      unhold,
      hangup,
      addAudioProcessor,
      removeAudioProcessor,
      resetCallState,
      acceptIncoming,
      rejectIncoming,
      selectIncomingLead,
      refreshReceiver,
    }),
    [
      connect,
      mute,
      unmute,
      hold,
      unhold,
      hangup,
      addAudioProcessor,
      removeAudioProcessor,
      resetCallState,
      acceptIncoming,
      rejectIncoming,
      selectIncomingLead,
      refreshReceiver,
    ]
  );

  return (
    <TwilioActionsContext.Provider value={actionsValue}>
      <TwilioStateContext.Provider value={stateValue}>
        {children}
      </TwilioStateContext.Provider>
    </TwilioActionsContext.Provider>
  );
};

/** Call state only — re-renders on call transitions. */
export const useTwilioState = () => {
  const ctx = useContext(TwilioStateContext);
  if (!ctx) throw new Error("useTwilioState must be used inside TwilioProvider");
  return ctx;
};

/** Call controls only — never re-renders (stable identity). */
export const useTwilioActions = () => {
  const ctx = useContext(TwilioActionsContext);
  if (!ctx) throw new Error("useTwilioActions must be used inside TwilioProvider");
  return ctx;
};

/**
 * Combined state + actions, the original API. Prefer useTwilioState /
 * useTwilioActions in new code when a component needs only one side.
 */
export const useTwilioContext = (): TwilioContextProps => {
  const state = useTwilioState();
  const actions = useTwilioActions();
  return useMemo(() => ({ ...state, ...actions }), [state, actions]);
};
